import { useState, useEffect, useRef, useCallback } from 'react';
import { CommunityPost } from '@/types';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { createClient } from '@/lib/supabase/browser';
import { logger } from '@/lib/logger';

export function useCommunityPosts() {
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthContext();
  const subscriptionRef = useRef<any>(null);
  const likingPostIdsRef = useRef<Set<string>>(new Set());

  const fetchCommunityPosts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const url = user?.id ? `/api/community/posts?userId=${user.id}` : '/api/community/posts';
      const response = await fetch(url);
      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[useCommunityPosts] API Error:', errorData);
        throw new Error(errorData.details || 'Failed to fetch posts');
      }
      
      const data = await response.json();
      setPosts(data || []);
    } catch (err) {
      logger.error('[useCommunityPosts] Error fetching posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load community posts');
      setPosts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchCommunityPosts();

    // Set up Supabase Realtime subscription for community_posts with debouncing
    const supabase = createClient();
    let reloadTimeout: NodeJS.Timeout;
    
    const channel = supabase
      .channel('community_posts_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'community_posts',
          filter: 'is_deleted=eq.false'
        },
        (payload: any) => {
          logger.log('[useCommunityPosts] Real-time post update:', payload.eventType);
          
          // Debounce rapid updates
          clearTimeout(reloadTimeout);
          
          if (payload.eventType === 'INSERT') {
            // New post created - fetch updated list after debounce
            reloadTimeout = setTimeout(() => {
              fetchCommunityPosts();
            }, 500);
          } else if (payload.eventType === 'UPDATE') {
            // Post updated - update in place if possible, otherwise refetch
            const updatedPost = payload.new as any;
            setPosts(prev => {
              const existingIndex = prev.findIndex(p => p.id === updatedPost.id);
              if (existingIndex >= 0) {
                // Update existing post
                const newPosts = [...prev];
                newPosts[existingIndex] = {
                  ...newPosts[existingIndex],
                  content: updatedPost.content,
                  media: updatedPost.media,
                  tags: updatedPost.tags || [],
                  updatedAt: updatedPost.updated_at
                };
                return newPosts;
              } else {
                // Post not in current list, refetch to get full data
                reloadTimeout = setTimeout(() => {
                  fetchCommunityPosts();
                }, 500);
                return prev;
              }
            });
          } else if (payload.eventType === 'DELETE') {
            // Post deleted - remove from list immediately
            setPosts(prev => prev.filter(p => p.id !== payload.old.id));
          }
        }
      )
      .subscribe((status: string) => {
        if (status === 'SUBSCRIBED') {
          logger.log('[useCommunityPosts] Subscribed to real-time updates');
        } else if (status === 'CHANNEL_ERROR') {
          logger.error('[useCommunityPosts] Error subscribing to real-time updates');
        }
      });

    subscriptionRef.current = channel;

    return () => {
      clearTimeout(reloadTimeout);
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [fetchCommunityPosts]);

  const createPost = async (content: string, media?: { type: 'image' | 'video' | 'audio'; url?: string; urls?: string[]; thumbnail?: string }, tags?: string[], category?: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/community/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        },
        body: JSON.stringify({ content, media, tags, category })
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[useCommunityPosts] Create post error:', errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to create post');
      }

      const newPost = await response.json();
      
      // Add to local state
      setPosts(prev => [newPost, ...prev]);
      return newPost;
    } catch (err) {
      logger.error('[useCommunityPosts] Error creating post:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to create post');
    }
  };

  const likePost = async (postId: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      if (likingPostIdsRef.current.has(postId)) {
        return;
      }
      likingPostIdsRef.current.add(postId);

      // Optimistic update
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              likes: post.isLiked ? Math.max(0, post.likes - 1) : post.likes + 1,
              isLiked: !post.isLiked 
            }
          : post
      ));

      const response = await fetch(`/api/community/posts/${postId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[useCommunityPosts] Like post error:', errorData);
        // Revert optimistic update on error
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                likes: post.isLiked ? post.likes + 1 : Math.max(0, post.likes - 1),
                isLiked: !post.isLiked 
              }
            : post
        ));
        throw new Error(errorData.details || errorData.error || 'Failed to like post');
      }

      const result = await response.json();
      
      // Update local state with server response
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              likes: result.likes_count ?? result.likes ?? 0,
              isLiked: result.liked ?? result.isLiked ?? false
            }
          : post
      ));
    } catch (err) {
      logger.error('[useCommunityPosts] Error liking post:', err);
    } finally {
      likingPostIdsRef.current.delete(postId);
    }
  };

  const savePost = async (postId: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`/api/community/posts/${postId}/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[useCommunityPosts] Save post error:', errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to save post');
      }

      const result = await response.json();
      
      // Update local state
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              saved: result.saved,
              isSaved: result.isSaved
            }
          : post
      ));
    } catch (err) {
      logger.error('[useCommunityPosts] Error saving post:', err);
      // Revert optimistic update on error
      setPosts(prev => prev.map(post => 
        post.id === postId 
          ? { 
              ...post, 
              saved: !post.saved,
              isSaved: !post.isSaved 
            }
          : post
      ));
    }
  };

  const deletePost = async (postId: string) => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      const response = await fetch(`/api/community/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[useCommunityPosts] Delete post error:', errorData);
        throw new Error(errorData.details || errorData.error || 'Failed to delete post');
      }

      // Remove from local state
      setPosts(prev => prev.filter(post => post.id !== postId));
    } catch (err) {
      logger.error('[useCommunityPosts] Error deleting post:', err);
      throw new Error(err instanceof Error ? err.message : 'Failed to delete post');
    }
  };

  const refetch = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/community/posts');
      if (!response.ok) {
        const errorData = await response.json();
        logger.error('[useCommunityPosts] API Error:', errorData);
        throw new Error(errorData.details || 'Failed to fetch posts');
      }
      
      const data = await response.json();
      setPosts(data || []);
    } catch (err) {
      logger.error('[useCommunityPosts] Error fetching posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load community posts');
    } finally {
      setLoading(false);
    }
  };

  return {
    posts,
    loading,
    error,
    createPost,
    likePost,
    savePost,
    deletePost,
    refetch,
    refreshPosts: fetchCommunityPosts
  };
} 